import { useEffect, useMemo } from "react";

import { Selector, type SelectorOption } from "../../components/ui";
import type { AgentAttribution, AgentRun } from "../api/e2e";

export const ALL_RUN_CREATORS = "all";
export type RunCreatorFilter = typeof ALL_RUN_CREATORS | string;

function creatorLabel(attribution: AgentAttribution): string {
  return (
    attribution.display_name?.trim() ||
    attribution.email?.trim() ||
    "Workspace user"
  );
}

export function runCreatorOptions(
  runs: readonly Pick<AgentRun, "started_by">[],
  currentSubject: string | null,
): readonly SelectorOption<RunCreatorFilter>[] {
  const creators = new Map<string, AgentAttribution>();
  for (const run of runs) {
    const attribution = run.started_by;
    if (!attribution || creators.has(attribution.subject)) continue;
    creators.set(attribution.subject, attribution);
  }
  const options = [...creators.values()]
    .sort((left, right) => {
      if (left.subject === currentSubject) return -1;
      if (right.subject === currentSubject) return 1;
      return creatorLabel(left).localeCompare(creatorLabel(right), undefined, {
        sensitivity: "base",
      });
    })
    .map((attribution) => {
      const isCurrentUser = attribution.subject === currentSubject;
      const label = creatorLabel(attribution);
      const email = attribution.email?.trim();
      const identityParts = [label, email, attribution.subject];
      return {
        value: attribution.subject,
        label: isCurrentUser ? "Me" : label,
        searchText: isCurrentUser
          ? identityParts
              .filter(
                (part, index) => part && identityParts.indexOf(part) === index,
              )
              .join(" ")
          : (email ?? attribution.subject),
        secondary: isCurrentUser
          ? email
          : email && email !== label
            ? email
            : undefined,
      } satisfies SelectorOption<RunCreatorFilter>;
    });
  return [{ value: ALL_RUN_CREATORS, label: "All creators" }, ...options];
}

export function runMatchesCreator(
  run: Pick<AgentRun, "started_by">,
  creator: RunCreatorFilter,
): boolean {
  return creator === ALL_RUN_CREATORS || run.started_by?.subject === creator;
}

export default function RunCreatorSelect({
  runs,
  currentSubject,
  value,
  onChange,
}: {
  runs: readonly Pick<AgentRun, "started_by">[];
  currentSubject: string | null;
  value: RunCreatorFilter;
  onChange: (value: RunCreatorFilter) => void;
}) {
  const options = useMemo(
    () => runCreatorOptions(runs, currentSubject),
    [currentSubject, runs],
  );
  useEffect(() => {
    if (
      value !== ALL_RUN_CREATORS &&
      !options.some((option) => option.value === value)
    ) {
      onChange(ALL_RUN_CREATORS);
    }
  }, [onChange, options, value]);
  return (
    <Selector
      options={options}
      value={value}
      onValueChange={onChange}
      searchable
      disabled={options.length === 1}
      ariaLabel="Created by"
      className="run-creator-select"
    />
  );
}
